import { useQuery } from '@tanstack/react-query';

import { api } from '../../lib/api';

export interface ProfileLink {
  platform: string;
  /** The server's label, never one the profile owner typed. See `ProfileLinks`. */
  label: string;
  url: string;
}

export interface LinkPlatform {
  platform: string;
  label: string;
  /** Empty means anything goes — that is `website`, the escape hatch. */
  hosts: string[];
  /**
   * Whether somebody can broadcast live video there.
   *
   * The gate on a host saying they are simulcasting: Spotify and Suno are
   * places to find music rather than places to watch somebody make it.
   */
  live: boolean;
}

/**
 * Which platforms a profile may link to.
 *
 * Fetched rather than hardcoded, for the reason `LinkOptionsAction` gives: a
 * picker with its own copy of the list can offer something the server rejects,
 * and the person who finds out is watching a save fail for a reason the form
 * said was fine.
 *
 * Cached hard. It is a list of well-known websites; it changes when somebody
 * edits a PHP file.
 */
export function useLinkPlatforms() {
  const { data } = useQuery({
    queryKey: ['link-options'],
    queryFn: () => api.get<{ platforms: LinkPlatform[]; max: number }>('/profile/link-options'),
    staleTime: Infinity,
    gcTime: Infinity,
  });

  return { platforms: data?.platforms ?? [], max: data?.max ?? 8 };
}

/**
 * Somebody's links, as a row of icons.
 *
 * ## Icons only, and what that costs
 *
 * The convention every artist profile shares, and it earns its place: eleven
 * recognisable marks read faster than eleven words, and they sit under the bio
 * without pushing the page around.
 *
 * What it costs is the name, so the name is given back twice - `aria-label` for
 * a screen reader, `title` for a hover. An icon row without those is a row of
 * unlabelled buttons to anybody not using their eyes, which is not a trade
 * worth making for tidiness.
 *
 * ## Website links are named by their host
 *
 * Two websites both draw the same globe, so "Website" twice would be a tooltip
 * that answers nothing. `bandcamp.com` and `myshop.example` tell you where you
 * are about to go, which is the entire question somebody hovers to ask.
 */
export function ProfileLinkRow({ links }: { links: ProfileLink[] }) {
  if (links.length === 0) return null;

  return (
    <div className="mt-4 flex flex-wrap items-center gap-2" data-profile-links>
      {links.map((link) => {
        const name = linkName(link);

        return (
          <a
            key={link.url}
            href={link.url}
            target="_blank"
            /*
             * `noopener` denies the opened page a handle on this one, which it
             * could otherwise use to navigate us somewhere convincing.
             * `nofollow` because these are user-submitted and this page would
             * otherwise be a link farm. The server also allowlists the scheme
             * to http/https, so `javascript:` never gets this far.
             */
            rel="noopener noreferrer nofollow"
            aria-label={name}
            title={name}
            data-profile-link={link.platform}
            className="flex h-9 w-9 items-center justify-center rounded-full border border-line text-soft transition-colors hover:border-line-strong hover:bg-raised hover:text-ink"
          >
            <PlatformIcon platform={link.platform} className="h-4 w-4" />
          </a>
        );
      })}
    </div>
  );
}

/** What to call a link out loud. See `ProfileLinkRow`. */
function linkName(link: ProfileLink): string {
  if (link.platform !== 'website') return link.label;

  try {
    return new URL(link.url).hostname.replace(/^www\./, '');
  } catch {
    // The server validated it, so this should not happen - but a tooltip is
    // not worth throwing on a profile page over.
    return link.label;
  }
}

/**
 * The glyph for a platform.
 *
 * Keyed by the same ids the server sends, and falling back to a globe for
 * anything it does not recognise. That fallback is the point of splitting the
 * icons from the catalogue: adding a platform server-side works immediately,
 * just plainly, instead of rendering a hole until the frontend catches up.
 *
 * Drawn as single paths at 24×24 rather than pulled from an icon package -
 * eleven glyphs is not worth a dependency, and brand marks in a package go
 * stale on somebody else's release schedule.
 */
export function PlatformIcon({ platform, className = 'h-4 w-4' }: { platform: string; className?: string }) {
  const path = GLYPHS[platform];

  if (!path) {
    return (
      <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden>
        <circle cx="12" cy="12" r="9" />
        <path d="M3 12h18M12 3c2.5 2.6 3.8 5.6 3.8 9s-1.3 6.4-3.8 9c-2.5-2.6-3.8-5.6-3.8-9S9.5 5.6 12 3z" />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden>
      <path d={path} />
    </svg>
  );
}

const GLYPHS: Record<string, string> = {
  tiktok:
    'M16.6 5.82A4.28 4.28 0 0 1 15.54 3h-3.09v12.4a2.59 2.59 0 0 1-2.59 2.5 2.59 2.59 0 0 1 0-5.18c.27 0 .52.04.76.12v-3.2a5.9 5.9 0 0 0-.76-.05A5.72 5.72 0 0 0 4.15 15.3 5.72 5.72 0 0 0 9.86 21a5.72 5.72 0 0 0 5.72-5.72V9.01a7.35 7.35 0 0 0 4.27 1.37V7.3a4.29 4.29 0 0 1-3.25-1.48z',
  instagram:
    'M12 2.16c3.2 0 3.58.01 4.85.07 1.17.05 1.8.25 2.23.41.56.22.96.48 1.38.9.42.42.68.82.9 1.38.16.42.36 1.06.41 2.23.06 1.27.07 1.65.07 4.85s-.01 3.58-.07 4.85c-.05 1.17-.25 1.8-.41 2.23-.22.56-.48.96-.9 1.38-.42.42-.82.68-1.38.9-.42.16-1.06.36-2.23.41-1.27.06-1.65.07-4.85.07s-3.58-.01-4.85-.07c-1.17-.05-1.8-.25-2.23-.41a3.8 3.8 0 0 1-1.38-.9 3.8 3.8 0 0 1-.9-1.38c-.16-.42-.36-1.06-.41-2.23-.06-1.27-.07-1.65-.07-4.85s.01-3.58.07-4.85c.05-1.17.25-1.8.41-2.23.22-.56.48-.96.9-1.38.42-.42.82-.68 1.38-.9.42-.16 1.06-.36 2.23-.41 1.27-.06 1.65-.07 4.85-.07zm0 5.88a3.96 3.96 0 1 0 0 7.92 3.96 3.96 0 0 0 0-7.92zm0 6.53a2.57 2.57 0 1 1 0-5.14 2.57 2.57 0 0 1 0 5.14zm5.04-6.69a.92.92 0 1 1-1.85 0 .92.92 0 0 1 1.85 0z',
  youtube:
    'M21.58 7.19a2.5 2.5 0 0 0-1.77-1.77C18.25 5 12 5 12 5s-6.25 0-7.81.42a2.5 2.5 0 0 0-1.77 1.77A26 26 0 0 0 2 12a26 26 0 0 0 .42 4.81 2.5 2.5 0 0 0 1.77 1.77C5.75 19 12 19 12 19s6.25 0 7.81-.42a2.5 2.5 0 0 0 1.77-1.77A26 26 0 0 0 22 12a26 26 0 0 0-.42-4.81zM10 15.02V8.98L15.2 12 10 15.02z',
  x: 'M17.53 3h3.02l-6.6 7.54L21.7 21h-6.07l-4.76-6.22L5.42 21H2.4l7.05-8.06L2.3 3h6.23l4.3 5.69L17.53 3zm-1.06 16.2h1.67L7.6 4.72H5.81l10.66 14.48z',
  spotify:
    'M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm4.59 14.42a.62.62 0 0 1-.86.21c-2.35-1.44-5.3-1.76-8.79-.96a.62.62 0 1 1-.28-1.22c3.81-.87 7.08-.5 9.72 1.11.29.18.39.57.21.86zm1.22-2.72a.78.78 0 0 1-1.07.26c-2.69-1.66-6.79-2.14-9.97-1.17a.78.78 0 1 1-.45-1.49c3.63-1.1 8.15-.57 11.23 1.33.37.23.48.71.26 1.07zm.11-2.84c-3.22-1.92-8.54-2.09-11.62-1.16a.93.93 0 1 1-.54-1.79c3.53-1.07 9.4-.86 13.11 1.34a.93.93 0 1 1-.95 1.61z',
  soundcloud:
    'M1.5 13.2c-.1 0-.17.07-.18.17l-.19 1.6.19 1.56c.01.1.08.17.18.17.09 0 .16-.07.18-.17l.22-1.56-.22-1.6c-.02-.1-.09-.17-.18-.17zm1.28-.9c-.11 0-.2.08-.21.19l-.25 2.48.25 2.4c.01.11.1.19.21.19s.2-.08.21-.19l.29-2.4-.29-2.48c-.01-.11-.1-.19-.21-.19zm1.35-.7c-.13 0-.23.1-.24.22l-.24 3.15.24 3.04c.01.13.11.22.24.22.12 0 .22-.1.24-.22l.27-3.04-.27-3.15a.24.24 0 0 0-.24-.22zm1.4-.35c-.14 0-.26.11-.27.25l-.22 3.47.22 3.34c.01.14.13.25.27.25s.25-.11.27-.25l.25-3.34-.25-3.47a.27.27 0 0 0-.27-.25zm1.45 0c-.16 0-.28.12-.3.28l-.2 3.44.2 3.32c.02.16.14.28.3.28s.28-.12.3-.28l.23-3.32-.23-3.44a.3.3 0 0 0-.3-.28zm1.5-1.6c-.17 0-.31.14-.32.31l-.19 5.03.19 3.29c.01.17.15.31.32.31s.31-.14.32-.31l.21-3.29-.21-5.03a.32.32 0 0 0-.32-.31zm1.53-1.05c-.19 0-.34.15-.35.34l-.17 6.05.17 3.25c.01.19.16.34.35.34s.34-.15.35-.34l.19-3.25-.19-6.05a.35.35 0 0 0-.35-.34zm1.6-.35c-.2 0-.37.17-.38.37l-.15 6.38.15 3.2c.01.2.18.37.38.37.21 0 .37-.17.38-.37l.17-3.2-.17-6.38a.38.38 0 0 0-.38-.37zm1.66 6.72.15 3.18a.4.4 0 0 0 .8 0l.16-3.18-.16-7.03a.4.4 0 0 0-.8 0l-.15 7.03zM21.2 12c-.44 0-.86.09-1.24.25A5.42 5.42 0 0 0 14.6 7.4c-.24 0-.44.2-.44.44v10.7c0 .24.19.44.43.45h6.6a2.5 2.5 0 0 0 0-5z',
  facebook:
    'M22 12.06C22 6.5 17.52 2 12 2S2 6.5 2 12.06c0 5.02 3.66 9.18 8.44 9.94v-7.03H7.9v-2.91h2.54V9.85c0-2.52 1.5-3.91 3.77-3.91 1.09 0 2.24.2 2.24.2v2.46h-1.26c-1.24 0-1.63.78-1.63 1.57v1.89h2.78l-.45 2.91h-2.33V22c4.78-.76 8.44-4.92 8.44-9.94z',
  twitch:
    'M4.3 3 3 6.5v12h4v2.5h2.5l2.5-2.5h3.5L21 13V3H4.3zm14.2 9.3-2.8 2.8h-3.5L9.7 17.6v-2.5H6.5V4.5h12v7.8zM15 7.4h1.7v4.8H15V7.4zm-4.4 0h1.7v4.8h-1.7V7.4z',
  discord:
    'M19.3 5.36A16.5 16.5 0 0 0 15.2 4.1a.06.06 0 0 0-.07.03c-.18.32-.38.73-.51 1.05a15.3 15.3 0 0 0-4.58 0c-.14-.33-.34-.73-.52-1.05a.06.06 0 0 0-.07-.03c-1.43.25-2.8.67-4.1 1.26a.06.06 0 0 0-.03.02C2.68 9.28 1.96 13.07 2.31 16.8a.07.07 0 0 0 .03.05 16.6 16.6 0 0 0 5 2.53.07.07 0 0 0 .07-.02c.39-.53.73-1.08 1.02-1.67a.06.06 0 0 0-.03-.09c-.54-.2-1.06-.45-1.56-.74a.06.06 0 0 1 0-.11l.31-.24a.06.06 0 0 1 .06 0 11.8 11.8 0 0 0 10.05 0 .06.06 0 0 1 .07 0l.3.24a.06.06 0 0 1 0 .1c-.5.3-1.02.55-1.56.75a.06.06 0 0 0-.03.09c.3.58.64 1.14 1.02 1.67a.07.07 0 0 0 .07.02 16.5 16.5 0 0 0 5.01-2.53.07.07 0 0 0 .03-.05c.42-4.32-.71-8.07-3-11.4a.05.05 0 0 0-.03-.03zM8.85 14.53c-.99 0-1.8-.9-1.8-2.01s.8-2.02 1.8-2.02c1 0 1.81.91 1.8 2.02 0 1.11-.8 2.01-1.8 2.01zm6.32 0c-.99 0-1.8-.9-1.8-2.01s.8-2.02 1.8-2.02c1 0 1.81.91 1.8 2.02 0 1.11-.8 2.01-1.8 2.01z',
};
